import React from 'react';
import { Grid } from '@mui/material';
import type { Hold, HoldList, Show } from 'api-client/models';
import type { useInlineEditing } from '../../hooks/useInlineEditing';
import type { BulkCopyResult } from '../../hooks/useBulkOperations';
import { shouldShowOperationalData } from '../../utils/validators';
import { DateTimingCard } from './timing/DateTimingCard';
import { TicketingAttendanceCard } from './timing/TicketingAttendanceCard';
import { FinancialSummaryCard } from './financial/FinancialSummaryCard';

type InlineEditing = ReturnType<typeof useInlineEditing>;
interface ShowDateDetailsProps {
  show: Show;
  hold: HoldList;
  holdDetails: Hold | null;
  editingSection: string | null;
  formData: InlineEditing['editFormData'];
  onStartEdit: InlineEditing['startEditing'];
  onSave: (section: string) => Promise<void>;
  onCancel: () => void;
  onUpdateField: (field: string, value: unknown) => void;
  onCopyTimes: () => Promise<BulkCopyResult>;
  onCopyExpenses: () => Promise<void>;
  otherConfirmedHoldCount: number;
  hasPendingHolds: boolean;
  userTimezone: string;
}

export const ShowDateDetails: React.FC<ShowDateDetailsProps> = ({
  show, hold, holdDetails, editingSection, formData, onStartEdit, onSave, onCancel, onUpdateField,
  onCopyTimes, onCopyExpenses, otherConfirmedHoldCount, hasPendingHolds, userTimezone,
}) => {
  const showOperationalData = shouldShowOperationalData(show);
  const timingForm = {
    loadInTime: formData.loadInTime, soundcheckTime: formData.soundcheckTime, doorsTime: formData.doorsTime,
    showTime: formData.showTime, endTime: formData.endTime,
  };
  const ticketingForm = { showCapacity: formData.showCapacity, attendance: formData.attendance };
  return (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      <Grid size={{ xs: 12, lg: 6 }}>
        <DateTimingCard
          hold={hold}
          holdDetails={holdDetails}
          isEditing={editingSection === 'timing'}
          formData={timingForm}
          onStartEdit={onStartEdit}
          onSave={onSave}
          onCancel={onCancel}
          onUpdateField={onUpdateField}
          onCopyToAll={async () => { await onCopyTimes(); }}
          otherConfirmedHoldCount={otherConfirmedHoldCount}
          hasPendingHolds={hasPendingHolds}
        />
      </Grid>
      {showOperationalData && (
        <Grid size={{ xs: 12, lg: 6 }}>
          <TicketingAttendanceCard
            hold={hold}
            holdDetails={holdDetails}
            show={show}
            isEditing={editingSection === 'ticketing'}
            formData={ticketingForm}
            onStartEdit={onStartEdit}
            onSave={onSave}
            onCancel={onCancel}
            onUpdateField={onUpdateField}
            userTimezone={userTimezone}
          />
        </Grid>
      )}
      {showOperationalData && (
        <Grid size={{ xs: 12 }}>
          <FinancialSummaryCard
            hold={hold}
            holdDetails={holdDetails}
            show={show}
            isEditing={editingSection === 'financial'}
            formData={formData}
            onStartEdit={onStartEdit}
            onSave={onSave}
            onCancel={onCancel}
            onUpdateField={onUpdateField}
            onCopyExpenses={onCopyExpenses}
            otherConfirmedHoldCount={otherConfirmedHoldCount}
            userTimezone={userTimezone}
          />
        </Grid>
      )}
    </Grid>
  );
};
